import type { BranchOrderDetail } from './api';

/**
 * Print a packing slip for a cashier order. We render it into a brand-new,
 * fully-isolated window rather than the app DOM so the cashier's thermal
 * receipt print stylesheet (`@page { size: 80mm }`, `.receipt-tape` visibility
 * rules in index.css) can't distort an A4/letter packing slip. The window is
 * self-contained HTML + inline CSS and closes itself after printing.
 */
export function printPackingSlip(order: BranchOrderDetail): void {
  const win = window.open('', '_blank', 'width=800,height=900');
  if (!win) return;

  const addr = (order.shipToAddress ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : '');
  const buyer = str(addr.fullName) || order.buyerName || order.buyerEmail || '';
  const phone = str(addr.phone);
  const addressLines = [
    str(addr.line1) || str(addr.street) || str(addr.address),
    str(addr.line2),
    [str(addr.barangay), str(addr.city) || str(addr.municipality)].filter(Boolean).join(', '),
    [str(addr.province) || str(addr.region), str(addr.postalCode) || str(addr.zip)].filter(Boolean).join(' '),
  ].filter(Boolean);

  const esc = (s: string) =>
    s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c));

  const itemRows = order.items
    .map(
      (it) => `<tr>
        <td>${esc(it.name)}${it.variantName ? ` <span class="muted">· ${esc(it.variantName)}</span>` : ''}</td>
        <td class="right">${it.qty}</td>
      </tr>`,
    )
    .join('');

  win.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Packing slip ${esc(order.code)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #111; margin: 0; padding: 24px; }
  h1 { font-size: 18px; letter-spacing: 0.02em; margin: 0; }
  .muted { color: #666; }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #ccc; padding-bottom: 12px; }
  .code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 14px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; padding: 16px 0; border-bottom: 1px solid #eee; }
  .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #888; margin-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 14px; }
  th, td { text-align: left; padding: 6px 4px; border-bottom: 1px solid #eee; }
  th.right, td.right { text-align: right; }
  @page { size: auto; margin: 12mm; }
</style>
</head>
<body>
  <header>
    <div>
      <h1>PACKING SLIP</h1>
      <p class="muted" style="font-size:12px;margin:4px 0 0">${esc(new Date(order.createdAt).toLocaleString())}</p>
    </div>
    <div style="text-align:right">
      <p class="code">${esc(order.code)}</p>
      <p class="muted" style="font-size:11px;text-transform:uppercase;margin:4px 0 0">${esc(order.type)}</p>
    </div>
  </header>
  <div class="grid">
    <div>
      <p class="label">Ship to</p>
      ${buyer ? `<p style="font-weight:600;margin:0">${esc(buyer)}</p>` : ''}
      ${addressLines.length ? addressLines.map((l) => `<p style="margin:2px 0">${esc(l)}</p>`).join('') : '<p class="muted">No shipping address on file.</p>'}
      ${phone ? `<p style="margin:4px 0 0">☎ ${esc(phone)}</p>` : ''}
    </div>
    <div>
      <p class="label">Ship from</p>
      ${order.branch ? `<p style="font-weight:600;margin:0">${esc(order.branch.name)}</p><p class="code muted" style="font-size:12px">${esc(order.branch.code)}</p>` : '<p class="muted">—</p>'}
      ${order.trackingNumber ? `<p style="font-size:12px;margin-top:8px"><span class="muted">Tracking: </span><span class="code">${esc(order.trackingNumber)}</span></p>` : ''}
    </div>
  </div>
  <p class="label" style="margin-top:16px">Items to pack</p>
  <table>
    <thead><tr><th>Item</th><th class="right">Qty</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
  <script>window.onload = function(){ window.print(); setTimeout(function(){ window.close(); }, 300); };</script>
</body>
</html>`);
  win.document.close();
}
